import { SQL, sql } from 'drizzle-orm';
import {
	type AnyPgColumn,
	bigint,
	boolean,
	foreignKey,
	pgEnum,
	pgSchema,
	text,
	timestamp,
	unique,
	uniqueIndex
} from 'drizzle-orm/pg-core';

export const appRole = pgEnum('app_role', ['admin', 'attendee']);
export const appPermission = pgEnum('app_permission', ['admin_access']);

export const auth = pgSchema('auth');

// Case-insensitive lower function for PostgreSQL
export function lower(column: AnyPgColumn): SQL {
	return sql`lower
		(${column})`;
}

export const users = auth.table(
	'users',
	{
		id: text('id').primaryKey(),
		name: text('name').notNull(),
		email: text('email'),
		emailVerified: boolean('email_verified')
			.$defaultFn(() => false)
			.notNull(),
		image: text('image'),
		createdAt: timestamp('created_at')
			.$defaultFn(() => /* @__PURE__ */ new Date())
			.notNull(),
		updatedAt: timestamp('updated_at')
			.$defaultFn(() => /* @__PURE__ */ new Date())
			.notNull(),
		role: text('role'),
		organizationId: text('organization_id'),
		banned: boolean('banned'),
		banReason: text('ban_reason'),
		banExpires: timestamp('ban_expires'),
		isAnonymous: boolean('is_anonymous'),
		termsAcceptedAt: timestamp('terms_accepted_at'),
		lastLoginAt: timestamp('last_login_at')
	},
	(table) => [
		uniqueIndex('organization_user_email').on(lower(table.email), table.organizationId),
		foreignKey({
			columns: [table.organizationId],
			foreignColumns: [organizations.id],
			name: 'users_organization_id_fkey'
		})
	]
);

export const sessions = auth.table('sessions', {
	id: text('id').primaryKey(),
	expiresAt: timestamp('expires_at').notNull(),
	token: text('token').notNull().unique(),
	createdAt: timestamp('created_at').notNull(),
	updatedAt: timestamp('updated_at').notNull(),
	ipAddress: text('ip_address'),
	userAgent: text('user_agent'),
	userId: text('user_id')
		.notNull()
		.references(() => users.id, { onDelete: 'cascade' }),
	impersonatedBy: text('impersonated_by'),
	activeOrganizationId: text('active_organization_id'),
	activeEventId: text('active_event_id'),
	featureFlags: text('feature_flags').array()
});

export const accounts = auth.table('accounts', {
	id: text('id').primaryKey(),
	accountId: text('account_id').notNull(),
	providerId: text('provider_id').notNull(),
	userId: text('user_id')
		.notNull()
		.references(() => users.id, { onDelete: 'cascade' }),
	accessToken: text('access_token'),
	refreshToken: text('refresh_token'),
	idToken: text('id_token'),
	accessTokenExpiresAt: timestamp('access_token_expires_at'),
	refreshTokenExpiresAt: timestamp('refresh_token_expires_at'),
	scope: text('scope'),
	password: text('password'),
	createdAt: timestamp('created_at').notNull(),
	updatedAt: timestamp('updated_at').notNull()
});

export const verifications = auth.table('verifications', {
	id: text('id').primaryKey(),
	identifier: text('identifier').notNull(),
	value: text('value').notNull(),
	expiresAt: timestamp('expires_at').notNull(),
	createdAt: timestamp('created_at').$defaultFn(() => /* @__PURE__ */ new Date()),
	updatedAt: timestamp('updated_at').$defaultFn(() => /* @__PURE__ */ new Date())
});

export const organizations = auth.table('organizations', {
	id: text('id').primaryKey(),
	name: text('name').notNull(),
	slug: text('slug').unique(),
	logo: text('logo'),
	createdAt: timestamp('created_at').notNull(),
	metadata: text('metadata'),
	// Demo and internal-testing orgs. Integration events are never emitted for
	// these, so our own usage doesn't show up as customer activity.
	isInternal: boolean('is_internal').default(false).notNull(),
	// Hides the org from the org switcher without touching its data. Most app
	// tables carry organization_id with no foreign key, so deleting the row
	// would orphan its events and orders rather than cascade.
	archivedAt: timestamp('archived_at')
});

export const members = auth.table('members', {
	id: text('id').primaryKey(),
	organizationId: text('organization_id')
		.notNull()
		.references(() => organizations.id, { onDelete: 'cascade' }),
	userId: text('user_id')
		.notNull()
		.references(() => users.id, { onDelete: 'cascade' }),
	role: text('role').default('member').notNull(),
	teamId: text('team_id'),
	createdAt: timestamp('created_at').notNull()
});

export const invitations = auth.table('invitations', {
	id: text('id').primaryKey(),
	organizationId: text('organization_id')
		.notNull()
		.references(() => organizations.id, { onDelete: 'cascade' }),
	email: text('email').notNull(),
	role: text('role'),
	teamId: text('team_id'),
	status: text('status').default('pending').notNull(),
	expiresAt: timestamp('expires_at').notNull(),
	inviterId: text('inviter_id')
		.notNull()
		.references(() => users.id, { onDelete: 'cascade' })
});

export const teams = auth.table('teams', {
	id: text('id').primaryKey(),
	name: text('name').notNull(),
	organizationId: text('organization_id')
		.notNull()
		.references(() => organizations.id, { onDelete: 'cascade' }),
	createdAt: timestamp('created_at').notNull(),
	updatedAt: timestamp('updated_at')
});

export const userRoles = auth.table(
	'user_roles',
	{
		id: bigint({ mode: 'number' }).primaryKey().generatedByDefaultAsIdentity({
			name: 'user_roles_id_seq',
			startWith: 1,
			increment: 1,
			minValue: 1,
			maxValue: 9223372036854775807,
			cache: 1
		}),
		userId: text('user_id').notNull(),
		role: appRole().notNull()
	},
	(table) => [
		foreignKey({
			columns: [table.userId],
			foreignColumns: [users.id],
			name: 'user_roles_user_id_fkey'
		}).onDelete('cascade'),
		unique('user_roles_user_id_role_key').on(table.userId, table.role)
	]
);

export const rolePermissions = auth.table(
	'role_permissions',
	{
		id: bigint({ mode: 'number' }).primaryKey().generatedByDefaultAsIdentity({
			name: 'role_permissions_id_seq',
			startWith: 1,
			increment: 1,
			minValue: 1,
			maxValue: 9223372036854775807,
			cache: 1
		}),
		role: appRole().notNull(),
		permission: appPermission().notNull()
	},
	(table) => [unique('role_permissions_role_permission_key').on(table.role, table.permission)]
);
